import { useCallback, useLayoutEffect, useRef, useState } from "react";

function readElementWidth(element: HTMLElement): number {
  return element.getBoundingClientRect().width;
}

/**
 * Tracks an element's content width via ResizeObserver.
 * Returns a callback ref so measurement starts once the node mounts.
 * Initial width is read synchronously in useLayoutEffect to avoid a visible layout flash.
 * ResizeObserver callbacks are coalesced with requestAnimationFrame and skip unchanged widths.
 */
export function useElementWidth(): readonly [(node: HTMLElement | null) => void, number] {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(0);
  const widthRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  const setElementRef = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  useLayoutEffect(() => {
    if (!element) {
      widthRef.current = 0;
      setWidth(0);
      return;
    }

    const commitWidth = () => {
      frameRef.current = null;
      const nextWidth = readElementWidth(element);

      if (nextWidth === widthRef.current) {
        return;
      }

      widthRef.current = nextWidth;
      setWidth(nextWidth);
    };

    const scheduleWidthCommit = () => {
      if (frameRef.current != null) {
        return;
      }

      frameRef.current = requestAnimationFrame(commitWidth);
    };

    commitWidth();

    const resizeObserver = new ResizeObserver(scheduleWidthCommit);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();

      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [element]);

  return [setElementRef, width] as const;
}
